import { ActionIcon, Group, Menu } from "@mantine/core";
import {
  IconChevronDown,
  IconChevronRight,
  IconDotsVertical,
  IconDownload
} from "@tabler/icons-react";
import * as _ from "lodash-es";
import {
  type MRT_ColumnDef,
  type MRT_RowSelectionState,
  MantineReactTable,
  useMantineReactTable
} from "mantine-react-table-open";
import { useCallback, useMemo, useState } from "react";
import api from "../../../api";
import { useNav } from "../../../hooks/useNav";
import { usePersons } from "../../../hooks/usePersons";
import { useProject } from "../../../hooks/useProject";
import { IconButton } from "../../../mantine/components/IconButton/IconButton";
import { getDefaultMRTOptions } from "../../../mantine/getDefaultMRTOptions";
import { useToast } from "../../../mantine/notifications/useToast";
import urls from "../../../urls";
import {
  type Priority,
  type Todo,
  TodoGroup,
  TodoStatus
} from "../../../utils/backend-types";
import { downloadCsvOrExcelFileForResponseWithContentDisposition } from "../../../utils/files/downloadCsvOrExcelFileForResponseWithContentDisposition";
import { LoadOrError } from "../../LoadOrError/LoadOrError";
import { useShouldShowStaffView } from "../../StaffViewToggle/useShouldShowStaffView";
import { useTodos } from "../../Todos/hooks/useTodos";
import { CLUSTERS } from "../../Todos/Todos.constants";
import { Mode } from "../../Todos/Todos.types";
import { getDefaultTodoTab } from "../../Todos/utils/getDefaultTodoTab";
import { getDocumentProvidedColumn } from "../Columns/getDocumentProvidedColumn";
import { getDueDateColumn } from "../Columns/getDueDateColumn";
import { getFulfillForColumn } from "../Columns/getFulfillForColumn";
import { getLabelColumn } from "../Columns/getLabelColumn";
import { getPersonColumn } from "../Columns/getPersonColumn";
import { getPriorityColumn } from "../Columns/getPriorityColumn";
import { getResponsibleColumn } from "../Columns/getResponsibleColumn";
import { getStatusColumn } from "../Columns/getStatusColumn";
import { TabValues } from "../TodosV2";
import { handleChangePriority } from "../utils/handleChangePriority";
import { handleChangeResponsible } from "../utils/handleChangeResponsible";
import { handleChangeStatus } from "../utils/handleChangeStatus";
import "./TodoTable.scss";

const enum TodosListColumnKeys {
  Group = "group",
  Cluster = "cluster",
  LabelForGrouping = "label-for-grouping",
  Label = "label",
  DueDate = "dueDate",
  Priority = "priority",
  Responsible = "responsible",
  FulfillFor = "fulfillFor",
  Person = "person",
  Status = "status",
  DocumentProvided = "documentProvided"
}

const groupLabelMapping = {
  [TodoGroup.ProjectPreparation]: "Projektvorbereitung",
  [TodoGroup.RegulatoryDuties]: "Pflichten"
};

interface TodosListProps {
  isPremium: boolean;
  mode: TabValues;
  variantId: number;
  projectId: string;
  onRowSelectionChange: (rowSelection: Array<number>) => void;
}

function TodoTable({
  isPremium,
  mode,
  variantId,
  projectId,
  onRowSelectionChange
}: TodosListProps) {
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

  const { shouldShowStaffView: isStaff } = useShouldShowStaffView();
  const { showErrorToast } = useToast();

  const {
    project,
    isLoading: projectLoading,
    error: projectError
  } = useProject(projectId);
  const users = project?.managers;

  const {
    todos,
    sortedTodos,
    isLoading: todosLoading,
    error: todosError,
    updateTodoResponsible,
    updateTodoPriority,
    updateTodoStatus
  } = useTodos({
    mode: Mode.Normal,
    variantId,
    isRecurring: mode === TabValues.Recurring,
    onUpdateError: (error) => {
      showErrorToast(error);
    }
  });

  const {
    persons,
    isLoading: personsLoading,
    error: personsError
  } = usePersons({ variantId });

  const navigate = useNav();

  const toggleTodoModal = (todo: Todo | false) => {
    if (todo) {
      navigate(`./${todo.id}/${getDefaultTodoTab(todo)}`);
    } else {
      navigate(".");
    }
  };

  const callHandleChangePriority = useCallback(
    (priorityValue: Priority, todoId: number) =>
      handleChangePriority(
        todos || [],
        updateTodoPriority,
        priorityValue,
        todoId
      ),
    [todos, updateTodoPriority]
  );

  const callHandleChangeResponsible = useCallback(
    (responsibleId: number | null, todoId: number) =>
      handleChangeResponsible(
        todos || [],
        updateTodoResponsible,
        responsibleId,
        todoId
      ),
    [todos, updateTodoResponsible]
  );

  const callHandleChangeStatus = useCallback(
    (status: TodoStatus, todoId: number) =>
      handleChangeStatus(todos || [], updateTodoStatus, status, todoId),
    [todos, updateTodoStatus]
  );

  function getSelectedAndVisibleTodos() {
    return todos
      ? todos.filter((todo) =>
          Object.keys(rowSelection).includes(todo.id.toString())
        )
      : [];
  }

  function getDownloadButtonLabel() {
    const selectedTodos = getSelectedAndVisibleTodos();
    const totalSelectedTodos = selectedTodos.length;
    if (totalSelectedTodos === 0) {
      return "";
    }

    let totalDocuments = 0;
    let numberOfTodosWithDocuments = 0;
    selectedTodos.forEach((todo) => {
      totalDocuments += todo.todoFiles.length;
      if (todo.todoFiles.length > 0) {
        numberOfTodosWithDocuments += 1;
      }
    });
    if (totalDocuments === 0) {
      return "";
    }
    const documentWording = totalDocuments === 1 ? "Dokument" : "Dokumente";
    return `${totalDocuments} ${documentWording} (in ${numberOfTodosWithDocuments} von ${totalSelectedTodos} Aufgaben)`;
  }
  const downloadButtonLabel = getDownloadButtonLabel();

  function handleDownloadSelectedTodoDocuments() {
    const selectedTodos = getSelectedAndVisibleTodos();
    const selectedTodoIds = selectedTodos.map((todo) => todo.id);
    if (selectedTodoIds && selectedTodoIds.length > 0) {
      const url = urls.api.downloadSelectedTodoDocuments(variantId);
      const body = {
        todoIds: selectedTodoIds
      };
      api
        .post(url, body)
        .then((response) =>
          downloadCsvOrExcelFileForResponseWithContentDisposition(
            response,
            true
          )
        )
        .catch((error) => {
          showErrorToast(error);
        });
    }
  }

  const columns: Array<MRT_ColumnDef<Todo>> = useMemo(() => {
    const groupingCols: Array<MRT_ColumnDef<Todo>> = [
      {
        accessorKey: TodosListColumnKeys.Group,
        header: "Gruppe",
        getGroupingValue: (row) => groupLabelMapping[row.group]
      },
      {
        accessorKey: TodosListColumnKeys.Cluster,
        header: "Kategorie",
        getGroupingValue: (row) => {
          const key = Object.keys(CLUSTERS).find(
            (clusterKey) => CLUSTERS[clusterKey].value === row.cluster
          );
          return key ? CLUSTERS[key].displayName : "";
        }
      }
    ];
    // label column for grouping only (hidden)
    const labelGroupingCol: MRT_ColumnDef<Todo> = {
      accessorKey: TodosListColumnKeys.LabelForGrouping,
      header: "Aufgabe",
      getGroupingValue: (row) => row.label
    };
    const commonCols = [
      getLabelColumn(),
      getDueDateColumn(todos || [], isPremium),
      getPriorityColumn(callHandleChangePriority),
      getResponsibleColumn(callHandleChangeResponsible, todos || [], users),
      getFulfillForColumn(todos || []),
      getPersonColumn(todos || []),
      getStatusColumn(callHandleChangeStatus)
    ];
    const documentProvidedCol = getDocumentProvidedColumn();

    if (isPremium) {
      return [...groupingCols, ...commonCols, documentProvidedCol];
    }
    return [...groupingCols, labelGroupingCol, ...commonCols];
  }, [
    todos,
    isPremium,
    users,
    callHandleChangePriority,
    callHandleChangeResponsible,
    callHandleChangeStatus
  ]);

  const defaultOptions = getDefaultMRTOptions<Todo>({
    emptyRowsFallbackText: "Keine Daten vorhanden",
    initialState: {
      columnFilters: [
        { id: TodosListColumnKeys.Status, value: [TodoStatus.Open] }
      ],
      expanded: true,
      grouping: isPremium
        ? [TodosListColumnKeys.Group, TodosListColumnKeys.Cluster]
        : [
            TodosListColumnKeys.Group,
            TodosListColumnKeys.Cluster,
            TodosListColumnKeys.LabelForGrouping
          ],
      showGlobalFilter: true
    },
    hideBorder: true
  });

  const tableOptions = _.merge({}, defaultOptions, {
    columnFilterDisplayMode: "popover",
    enableBottomToolbar: false,
    enableGrouping: true,
    enablePagination: false,
    enableRowSelection: isStaff,
    enableRowVirtualization: true,
    enableSorting: false,
    groupedColumnMode: "remove",
    positionToolbarAlertBanner: "none",
    enableFacetedValues: true,
    state: {
      rowSelection
    },
    onRowSelectionChange: (newSelection) => {
      setRowSelection(newSelection);
      if (typeof newSelection === "function") {
        onRowSelectionChange(
          Object.keys(newSelection(rowSelection)).map((idAsString) =>
            parseInt(idAsString)
          )
        );
      }
    },
    mantineExpandButtonProps: ({ row }) => ({
      size: row.groupingColumnId === TodosListColumnKeys.Group ? 0 : "sm",
      children: row.getIsExpanded() ? <IconChevronDown /> : <IconChevronRight />
    }),
    /* hide grouping cells and useless chevrons */
    mantineTableHeadCellProps: ({ column }) => ({
      display: column.getIndex() === 0 ? "none" : undefined
    }),
    /* hide grouping cells and useless chevrons */
    mantineTableBodyCellProps: ({ row, column }) => ({
      onClick: () => {
        if (
          row.groupingColumnId === TodosListColumnKeys.Cluster ||
          row.groupingColumnId === TodosListColumnKeys.LabelForGrouping
        ) {
          row.toggleExpanded();
        } else if (column.id === TodosListColumnKeys.Label) {
          toggleTodoModal(row.original);
        }
      },
      display:
        (row.getCanExpand() && column.getIndex() !== 0) ||
        (!row.getCanExpand() && column.getIndex() === 0)
          ? "none"
          : undefined,
      style: {
        fontWeight: row.getCanExpand() ? "bold" : "normal",
        cursor:
          row.groupingColumnId === TodosListColumnKeys.Cluster ||
          row.groupingColumnId === TodosListColumnKeys.LabelForGrouping ||
          column.id === TodosListColumnKeys.Label
            ? "pointer"
            : "default"
      }
    }),
    enableTopToolbar: true,
    renderTopToolbarCustomActions: () => (
      <Group justify="flex-end" w="stretch">
        <IconButton
          className="download-selected-documents-button"
          disabled={!downloadButtonLabel}
          icon={IconDownload}
          onClick={handleDownloadSelectedTodoDocuments}
        >
          Dokumente herunterladen
        </IconButton>
        <Menu>
          <Menu.Target>
            <ActionIcon
              aria-label="Mehr..."
              className="toggle-expand"
              color="neutral"
              variant="transparent"
            >
              <IconDotsVertical />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              onClick={() =>
                table.setExpanded(
                  Object.assign(
                    {},
                    ...table
                      .getCenterRows()
                      .filter((row) => row.getCanExpand())
                      .map((row) => ({ [row.id]: true }))
                  )
                )
              }
            >
              Alle ausklappen
            </Menu.Item>
            <Menu.Item
              onClick={() =>
                table.setExpanded(
                  Object.assign(
                    {},
                    ...table
                      .getCenterRows()
                      .filter(
                        (row) =>
                          row.groupingColumnId === TodosListColumnKeys.Group
                      )
                      .map((row) => ({ [row.id]: true }))
                  )
                )
              }
            >
              Alle reduzieren
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>
    )
  });

  const table = useMantineReactTable({
    ...tableOptions,
    columns,
    data: sortedTodos || []
  });

  return (
    <div className="TodoTable">
      <LoadOrError
        error={projectError || personsError || todosError}
        loading={projectLoading || personsLoading || todosLoading}
      >
        {project && persons && todos && (
          <>
            <MantineReactTable table={table} />
          </>
        )}
      </LoadOrError>
    </div>
  );
}

export { TodoTable, TodosListColumnKeys, TodosListProps };
